import React, {Component, useEffect, useRef, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import axios from 'axios'
import Helper, {JOB} from "../../../Shared/Helper";
import {RequestState} from "../../../Shared/StateHelper";
import {TimeRange} from "../../../Shared/Enums/Enums";
import Loader from "../../../Shared/Loader";
import BorderedDisplay from "../../../Shared/UiKit/BorderedDisplay";
import {t} from "../../../I18n/i18n"

const RESULT_PER_PAGE = 30
const PATIENT_SEARCH_DELAY = 400
const EMPTY_FILTERS = {orthoptist_id: "", start_date: "", end_date: "", status: "", sms: "", patient: ""}
const STATUS_OPTIONS = [
    {value: "0", label: "N/A"},
    {value: "1", label: t("OPHTALMO_HISTORY.PROCESS_OF_VALIDATION")},
    {value: "2", label: t("OPHTALMO_HISTORY.VALIDATED_AND_ACCESSIBLE")},
    {value: "3", label: t("ALERT")}
]
const FIELD_STYLE = {
    width: "100%",
    height: "3rem",
    margin: 0,
    padding: "0 10px",
    boxSizing: "border-box",
    border: "1px solid var(--separator-color)",
    borderRadius: 2,
    backgroundColor: "var(--surface-color)",
    color: "var(--font-color-main)",
    fontSize: "1rem"
}
const SMS_OPTIONS = [
    {value: "pending", label: t("PENDING")},
    {value: "sent", label: t("SENT")}
]

export default function Log() {
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [patientQuery, setPatientQuery] = useState("")
    const [period, setPeriod] = useState("")
    const [orthoptists, setOrthoptists] = useState([])
    const [data, setData] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [requestState, setRequestState] = useState(RequestState.LOADING)
    const requestId = useRef(0)

    useEffect(() => {
        axios.get("/admin/logs/appointments/orthoptists")
            .then((response) => setOrthoptists(response.data))
            .catch(() => setOrthoptists([]))
    }, [])

    useEffect(() => {
        const timeout = setTimeout(() => {
            const patient = patientQuery.trim()
            setFilters((previous) => previous.patient === patient ? previous : {...previous, patient: patient})
        }, PATIENT_SEARCH_DELAY)
        return () => clearTimeout(timeout)
    }, [patientQuery])

    useEffect(() => {
        setRequestState(RequestState.LOADING)
        loadData(1, true)
    }, [filters])

    const loadData = (pageToLoad, reset = false) => {
        const currentRequest = ++requestId.current
        axios.get("/admin/logs/appointments/data", {params: {page: pageToLoad, ...filters}})
            .then((response) => {
                if (currentRequest !== requestId.current) return
                const appointments = response.data.appointments
                setData((previous) => reset ? appointments : previous.concat(appointments))
                setTotalCount(response.data.total_count)
                setPage(pageToLoad + 1)
                setHasMore(appointments.length === RESULT_PER_PAGE)
                setRequestState(RequestState.SUCCESS)
            })
            .catch(() => {
                if (currentRequest !== requestId.current) return
                setRequestState(RequestState.ERROR)
            })
    }

    const handleFilterChange = (field, value) => {
        setFilters((previous) => ({...previous, [field]: value}))
    }

    const handleDateChange = (field, value) => {
        setPeriod("")
        handleFilterChange(field, value)
    }

    const handlePeriodChange = (value) => {
        setPeriod(value)
        const range = TimeRange[value]
        if (!range) return
        setFilters((previous) => ({...previous, start_date: range.start(), end_date: range.end()}))
    }

    const resetFilters = () => {
        setPatientQuery("")
        setPeriod("")
        setFilters(EMPTY_FILTERS)
    }

    const renderField = (label, field, flex = "1 1 200px") => {
        return <div style={{display: "flex", flexDirection: "column", gap: 5, flex: flex}}>
            <label>{label}</label>
            {field}
        </div>
    }

    const renderSelect = (field, defaultLabel, options) => {
        return <select className="browser-default" style={FIELD_STYLE} value={filters[field]}
                       onChange={(e) => handleFilterChange(field, e.target.value)}>
            <option value="">{defaultLabel}</option>
            {options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </select>
    }

    const renderDateInput = (field, min, max) => {
        return <input type="date" className="browser-default" style={FIELD_STYLE}
                      value={filters[field]} min={min || undefined} max={max || undefined}
                      onChange={(e) => handleDateChange(field, e.target.value)}/>
    }

    const renderFilters = () => {
        const hasActiveFilters = patientQuery !== "" || Object.values(filters).some((value) => value !== "")
        const orthoptistOptions = orthoptists.map((orthoptist) => ({
            value: orthoptist.id,
            label: Helper.formatName(orthoptist.nom, orthoptist.prenom)
        }))
        const periodOptions = Object.values(TimeRange).map((range) => ({value: range.value, label: range.name}))
        return <BorderedDisplay text={t("OPHTALMO_HISTORY.FILTERS")} containerStyle={{marginTop: 20, marginBottom: 10}}>
            <div style={{display: "flex", flexDirection: "column", gap: 15}}>
                <div style={{display: "flex", flexWrap: "wrap", gap: 20}}>
                    {renderField(t("PATIENT"),
                        <input type="text" className="browser-default" style={FIELD_STYLE} value={patientQuery}
                               placeholder={t("OPHTALMO_HISTORY.PATIENT_PLACEHOLDER")}
                               onChange={(e) => setPatientQuery(e.target.value)}/>)}
                    {renderField(t("ORTHOPTIST"), renderSelect("orthoptist_id", t("OPHTALMO_HISTORY.ALL_ORTHOPTISTS"), orthoptistOptions))}
                    {renderField(t("STATUS"), renderSelect("status", t("OPHTALMO_HISTORY.ALL_STATUSES"), STATUS_OPTIONS))}
                    {renderField(t("OPHTALMO_HISTORY.SMS"), renderSelect("sms", t("OPHTALMO_HISTORY.ALL_SMS"), SMS_OPTIONS))}
                </div>
                <div style={{display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-end"}}>
                    {renderField(t("OPHTALMO_HISTORY.ANALYSE_PERIOD"),
                        <select className="browser-default" style={FIELD_STYLE} value={period}
                                onChange={(e) => handlePeriodChange(e.target.value)}>
                            <option value="">{t("OPHTALMO_HISTORY.CUSTOM_PERIOD")}</option>
                            {periodOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>)}
                    {renderField(`${t("OPHTALMO_HISTORY.ANALYSE_DATE")} - ${t("FROM").toLowerCase()}`,
                        renderDateInput("start_date", null, filters.end_date))}
                    {renderField(t("TO"), renderDateInput("end_date", filters.start_date, null))}
                    <div style={{display: "flex", flex: "1 1 200px", justifyContent: "flex-end"}}>
                        <a className={`btn-flat blue-text text-darken-2 valign-wrapper ${hasActiveFilters ? "" : "disabled"}`}
                           onClick={resetFilters}>
                            <i className="material-icons left">clear</i>
                            {t("OPHTALMO_HISTORY.RESET")}
                        </a>
                    </div>
                </div>
            </div>
        </BorderedDisplay>
    }

    const renderResultsCount = () => {
        if (requestState !== RequestState.SUCCESS || totalCount === 0) return null
        return <p className="grey-text text-darken-1" style={{margin: "0 0 15px 10px"}}>
            {t("OPHTALMO_HISTORY.RESULTS_COUNT", {count: totalCount})}
        </p>
    }

    const renderTable = () => {
        if (requestState === RequestState.LOADING) return <Loader/>
        if (requestState === RequestState.ERROR) return <div
            className="center">{t("OPHTALMO_HISTORY.ERROR_HAS_OCCURED")}</div>
        if (!data.length) return <div className="center">{t("OPHTALMO_HISTORY.NO_RESULT")}</div>
        return <InfiniteScroll
            dataLength={data.length}
            next={() => loadData(page)}
            hasMore={hasMore}
            loader={<h4 className="center">{t("OPHTALMO_HISTORY.LOADING")}</h4>}
        >
            <table className="striped bordered">
                <thead>
                <tr>
                    <th className="center"> {t("DATE")}</th>
                    <th className="center"> {t("PATIENT")}</th>
                    <th className="center"> {JOB}</th>
                    <th className="center"> {t("STATUS")}</th>
                    <th className="center"> {t("OPHTALMO_HISTORY.ANALYSE_DATE")}</th>
                    <th className="center"> {t("OPHTALMO_HISTORY.SMS_SENT")}</th>
                </tr>
                </thead>
                <tbody>
                {data.map((appointment) => (
                    <tr key={appointment.id}>
                        <td>
                            {Helper.formatDate(appointment.start, "DD-MM-YYYY HH:mm")}
                        </td>
                        <td>
                            <a className="btn-flat blue-text text-darken-2"
                               href={"/patients/" + appointment.patient.id}
                               target="_blank">
                                {Helper.formatName(appointment.patient.prenom, appointment.patient.nom)}
                            </a>
                        </td>
                        <td>
                            {Helper.formatName(appointment.orthoptiste.nom, appointment.orthoptiste.prenom)}
                        </td>
                        <td>
                            <StatusApp statut={appointment.status_controle} unlock={appointment.override_lock}/>
                        </td>
                        <td>
                            {Helper.formatDate(appointment.analisis_date, "DD-MM-YYYY HH:mm")}
                        </td>
                        <td>
                            {appointment.sms ? (
                                <span>
                                    <i className="material-icons left orange-text">more_time</i> {t("PENDING")}</span>
                            ) : (
                                <span>
                                    <i className="material-icons left green-text">done</i> {t("SENT")}</span>
                            )}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </InfiniteScroll>
    }

    return (
        <div className="container">
            <h1 className="center">{t("OPHTALMO_HISTORY.TITLE")}</h1>
            {renderFilters()}
            {renderResultsCount()}
            {renderTable()}
        </div>
    )
}


export class StatusApp extends Component {
    constructor(props) {
        super(props);
    }

    render() {
        const {statut, unlock} = this.props;
        if (statut == 0) {

            return (
                <React.Fragment>
                    <i className="material-icons left">announcement</i> N/A
                </React.Fragment>
            );
        } else if (statut == 1) {
            return (
                <React.Fragment>
                    <i className="material-icons left orange-text">pending</i> {t("OPHTALMO_HISTORY.PROCESS_OF_VALIDATION")}
                </React.Fragment>
            );
        } else if (statut == 2) {
            return (
                <React.Fragment>
                    <i className="material-icons left green-text">done</i> {t("OPHTALMO_HISTORY.VALIDATED_AND_ACCESSIBLE")}
                </React.Fragment>
            );
        } else {
            return (
                <React.Fragment>
                    <i className="material-icons left red-text">report_problem</i> {t("ALERT")}
                    {unlock ? (
                        <span> {t("OPHTALMO_HISTORY.BUT_UNLOCK")}</span>
                    ) : (
                        <span></span>
                    )}
                </React.Fragment>
            );
        }
    }

}
